import firebase from './firebase';

// Mirrors the iOS app's login (LoginViewController.swift): the stored password is
// either plaintext or AES-128-CBC (PKCS7, base64) using the shared key and the
// per-user `encryptionIV`.
const ENCRYPTION_KEY = "a5d4e0ccl21o4e68";
const SESSION_KEY = "stew_session";

const utf8 = (s) => new TextEncoder().encode(s);

const base64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

const decryptPassword = async (encrypted, iv) => {
  const key = await window.crypto.subtle.importKey(
    'raw', utf8(ENCRYPTION_KEY), { name: 'AES-CBC' }, false, ['decrypt']
  );
  const plain = await window.crypto.subtle.decrypt(
    { name: 'AES-CBC', iv: utf8(iv) }, key, base64ToBytes(encrypted)
  );
  return new TextDecoder().decode(plain);
};

const passwordMatches = async (stored, iv, attempt) => {
  if (!iv) return stored === attempt;
  try {
    return (await decryptPassword(stored, iv)) === attempt;
  } catch (e) {
    return false;
  }
};

// "Remember me" sessions live in localStorage; otherwise they're kept in sessionStorage and
// end with the tab.
const readStored = (storage) => {
  try {
    const session = JSON.parse(storage.getItem(SESSION_KEY));
    return session && session.username ? session : null;
  } catch (e) {
    return null;
  }
};

const storageFor = (remember) => (remember ? window.localStorage : window.sessionStorage);

export const getSession = () => {
  try {
    return readStored(window.sessionStorage) || readStored(window.localStorage);
  } catch (e) {
    return null;
  }
};

export const updateSession = (changes) => {
  const session = getSession();
  if (!session) return;
  try {
    const remembered = !!readStored(window.localStorage);
    storageFor(remembered).setItem(SESSION_KEY, JSON.stringify({ ...session, ...changes }));
  } catch (e) { /* storage unavailable */ }
};

export const logout = () => {
  try { window.localStorage.removeItem(SESSION_KEY); } catch (e) { /* storage unavailable */ }
  try { window.sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* storage unavailable */ }
};

// Resolves with the session on success; rejects with a user-facing Error.
export const login = async (username, password, remember = true) => {
  if (!username || !password) {
    throw new Error("One or more fields is empty.");
  }

  let user;
  try {
    // Usernames become DB paths, so reject characters Firebase disallows rather than throwing.
    if (/[.#$\[\]\/]/.test(username)) throw new Error("invalid");
    const snap = await firebase.database().ref('users').child(username).once('value');
    user = snap.val();
  } catch (e) {
    if (e.message === "invalid") throw new Error("User/password combination is incorrect.");
    throw new Error("Server error encountered.");
  }

  if (!user || !user.username || typeof user.password !== 'string' ||
      !(await passwordMatches(user.password, user.encryptionIV, password))) {
    throw new Error("User/password combination is incorrect.");
  }

  const session = { username: user.username, name: user.name || "" };
  logout();
  try {
    storageFor(remember).setItem(SESSION_KEY, JSON.stringify(session));
  } catch (e) { /* session just won't persist across reloads */ }
  return session;
};
